// =============================================================================
// src/lib/utils.ts
// Pure utility functions — no side effects, no state.
// Sections 4–7 from the original App.tsx.
// =============================================================================

import { Order, OrderStatus } from './types';
import { STATUS_CYCLE } from './constants';

// =============================================================================
// SECTION 4: DATE FORMATTING
// Converts an ISO date string into the regional format set in Settings.
// Returns "—" for empty or invalid dates rather than crashing.
// =============================================================================

export function formatDate(iso: string, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(iso));
  } catch { return iso; }
}

// =============================================================================
// SECTION 5: STATUS CYCLING
// Pipeline: Pending → Ordered → Delivered → Closed → Pending (wraps).
// All orders use the same cycle regardless of category (F-001: removed the
// category-name branch — `order_items[].category` is no longer read here).
// =============================================================================

export function nextStatus(order: Order): OrderStatus {
  const idx = STATUS_CYCLE.indexOf(order.status);
  return STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
}

// =============================================================================
// SECTION 6: CURRENCY FORMATTING
// Single source of truth for money rendering across the app.
// `currency` is the user-configured symbol prefix (e.g. '$', 'NZ$', '£') —
// we keep it as a separate prefix because the app supports multi-currency.
// `mode` 'full' shows two decimals ($105,533.52); 'rounded' shows none
// ($105,534) for chart axes / headline numbers where the cents are noise.
// =============================================================================

export function formatCurrency(
  value: number,
  currency: string = '$',
  mode: 'full' | 'rounded' = 'full',
): string {
  const digits = mode === 'full' ? 2 : 0;
  const formatted = new Intl.NumberFormat('en-NZ', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value || 0);
  return `${currency}${formatted}`;
}
